import React, { useState, useEffect, useRef } from 'react';
import { LayoutGrid, ClipboardList, LineChart, Puzzle, RotateCcw, LogOut, User, CreditCard, BookOpen, GraduationCap, MessageCircle, Bell, Calendar, Wallet, Users, ArrowRight, Menu, X, Eye, EyeOff, Lock, Check } from 'lucide-react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { verificarHijos } from '../services/matriculaService';
import { obtenerCentro } from '../services/centroService';
import { obtenerFechasPago } from '../services/pagosServices';
import Notificaciones from './notificaciones';
import Chatbot from './chatbot';
import Chat from './chat';
import useMessageNotifications from '../hooks/useMessageNotifications';
import moment from 'moment';
import 'moment/locale/es';  // Importamos el locale español
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerActividadesNino, obtenerActividadesExtracurricularesPorNino } from '../services/ActividadesService';
import { obtenerEvaluacionesNino } from '../services/evaluacionesService';
import { actualizarUsuarioPerfil } from '../services/matriculaService';

const ParentDashboard = ({ user, handleLogout }) => {
  const [hijos, setHijos] = useState([]);
  const [hijoSeleccionado, setHijoSeleccionado] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [nombreCentro, setNombreCentro] = useState('Centro Infantil');
  const [fechaLimitePago, setFechaLimitePago] = useState(null);
  const [periodoActual, setPeriodoActual] = useState('');
  const [estadoPagos, setEstadoPagos] = useState({ 
    pagosPendientes: 0,
    transferenciasEnRevision: 0
  });
  const [actividadesPendientes, setActividadesPendientes] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(true);
  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const location = useLocation();
  const passwordIsCedula = localStorage.getItem('passwordIsCedula') === 'true';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const passwordIsCedulaInitial = localStorage.getItem('passwordIsCedula') === 'true';
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(passwordIsCedulaInitial);
  const [nuevoPassword, setNuevoPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiResponseMessage, setApiResponseMessage] = useState({ text: '', type: '' });
  const [isPasswordSuccessfullyChanged, setIsPasswordSuccessfullyChanged] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    letter: false,
    number: false,
    special: false
  });
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');

  const {
    mensajesNoLeidos,
    mensajesNuevos,
    showChat,
    handleChatOpen,
    handleChatClose,
    handleMessageRead
  } = useMessageNotifications(user);

  // Configuramos moment en español
  moment.locale('es');

  // Password change modal logic
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 15;
    if (password.length >= 10) strength += 10;
    if (password.length >= 12) strength += 10;
    if (password.match(/[a-z]/)) strength += 10;
    if (password.match(/[A-Z]/)) strength += 10;
    const numbers = password.match(/\d/g) || [];
    if (numbers.length >= 1) strength += 10;
    if (numbers.length >= 3) strength += 10;
    const specials = password.match(/[!@#$%^&*(),.?":{}|<>]/g) || [];
    if (specials.length >= 1) strength += 15;
    if (specials.length >= 2) strength += 10;
    return Math.min(strength, 100); // Cap at 100
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 40) return 'bg-red-500';
    if (strength < 60) return 'bg-orange-500';
    if (strength < 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 40) return 'Muy débil';
    if (strength < 60) return 'Débil';
    if (strength < 80) return 'Moderada';
    if (strength < 95) return 'Fuerte';
    return 'Muy fuerte';
  };

  const validatePasswordCriteria = (password) => {
    setPasswordCriteria({
      length: password.length >= 8,
      letter: /[a-zA-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-700' : 'text-slate-500'}`}>
      {met ? <Check size={16} className="text-green-500" /> : <div className="w-3 h-3 border border-slate-400 rounded-full" />}
      <span>{text}</span>
    </div>
  );

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setApiResponseMessage({ text: '', type: '' }); // Clear previous API message

    if (nuevoPassword !== confirmarPassword) {
      setPasswordChangeError('Las contraseñas no coinciden.');
      return;
    }
    if (!isValidPassword || passwordStrength < 75) {
      setPasswordChangeError('La contraseña no cumple con todos los requisitos de seguridad o es demasiado débil.');
      return;
    }
    if (passwordStrength <= 40 && nuevoPassword.length > 0) { // Check strength only if password is not empty
        setPasswordChangeError('La contraseña es demasiado débil. Por favor, intente una más compleja.');
        return;
    }

    try {
      const userId = user.id;
      const response = await actualizarUsuarioPerfil(userId, { password_us: nuevoPassword });

      if (response.success || response.message === 'Usuario actualizado correctamente') {
      localStorage.setItem('passwordIsCedula', 'false');
      const event = new Event('storage'); // Dispatch generic storage event
      window.dispatchEvent(event);
      setApiResponseMessage({ text: 'Contraseña actualizada correctamente.', type: 'success' });
      setIsPasswordSuccessfullyChanged(true);
      setPasswordChangeError(''); // Clear any previous validation error
    } else {
      setApiResponseMessage({ text: response.message || 'Error al actualizar la contraseña. Intente nuevamente.', type: 'error' });
      setIsPasswordSuccessfullyChanged(false);
    }
  } catch (error) {
    console.error("Error en handlePasswordChangeSubmit (Padre):", error);
    setApiResponseMessage({ text: error.response?.data?.message || error.message || 'Error de conexión. Verifique su red e intente nuevamente.', type: 'error' });
    setIsPasswordSuccessfullyChanged(false);
  }
};

  const handleCloseModalAfterSuccess = () => {
    setShowPasswordChangeModal(false);
    setNuevoPassword('');
    setConfirmarPassword('');
    setPasswordStrength(0);
    setPasswordCriteria({ length: false, letter: false, number: false, special: false });
    setApiResponseMessage({ text: '', type: '' });
    setPasswordChangeError('');
    setIsPasswordSuccessfullyChanged(false);
  };

  useEffect(() => {
    const cargarNombreCentro = async () => {
      try {
        const response = await obtenerCentro();
        if (response.success && response.data.length > 0) {
          setNombreCentro(`Centro Infantil ${response.data[0].nombre_unidad}`);
        }
      } catch (error) {
        console.error('Error al cargar el nombre del centro:', error);
      }
    };

    cargarNombreCentro();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.', {
          onClose: () => handleLogout(),
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    };
    checkTokenExpiration();
  }, [handleLogout]);

  useEffect(() => {
    const padreId = JSON.parse(localStorage.getItem('user'))?.id;

    if (padreId) {
      const fetchHijos = async () => {
        try {
          const response = await verificarHijos(padreId);
          const hijosActivos = (response.hijos || []).filter(hijo => hijo.estado_matricula === 'Activa');
          
          // Verificar estado de pagos para cada hijo
          const hijosConPago = await Promise.all(
            hijosActivos.map(async (hijo) => {
              try {
                const fechasResponse = await obtenerFechasPago(hijo.id_matricula);
                const fechaActual = new Date();
                fechaActual.setHours(0, 0, 0, 0);
                const ultimoRegistro = fechasResponse.data && fechasResponse.data.length > 0 
                  ? fechasResponse.data[fechasResponse.data.length - 1] 
                  : null;
                const proximaFechaPago = ultimoRegistro ? new Date(ultimoRegistro.proxima_fecha_pago) : null;
                
                let estadoPago = 'pendiente';
                let requierePago = true;
                
                if (ultimoRegistro) {
                  // Verificar si hay un pago en proceso de verificación (transferencia pendiente)
                  const tieneTransferenciaPendiente = ultimoRegistro.tipo_pago === 'Transferencia' && 
                                                     ultimoRegistro.estado_verificacion === 'Pendiente' &&
                                                     ultimoRegistro.comprobante_pago;
                  
                  if (tieneTransferenciaPendiente) {
                    estadoPago = 'revision';
                    requierePago = false; // No requiere pago si hay transferencia en revisión
                  } else if (ultimoRegistro.estado_verificacion === 'Verificado') {
                    // Pago verificado - verificar si necesita próximo pago
                    estadoPago = 'completado';
                    requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : false;
                  } else if (ultimoRegistro.estado_verificacion === 'Rechazado') {
                    // Pago rechazado - siempre requiere pago
                    estadoPago = 'pendiente';
                    requierePago = true;
                  } else {
                    // Estado pendiente sin transferencia o matrícula nueva
                    // Verificar fecha de pago para determinar si está vencido
                    if (proximaFechaPago) {
                      requierePago = fechaActual >= proximaFechaPago;
                      estadoPago = requierePago ? 'pendiente' : 'al_dia';
                    } else {
                      requierePago = true;
                      estadoPago = 'pendiente';
                    }
                  }
                } else {
                  requierePago = true; // Si no hay registro, requiere pago
                  estadoPago = 'sin_control';
                }
                
                return {
                  ...hijo,
                  estadoPago,
                  requierePago
                };
              } catch (error) {
                console.warn(`No se encontraron fechas de pago para la matrícula ${hijo.id_matricula}:`, error.message);
                // Si no hay control de pagos, significa que nunca se ha pagado, por lo tanto requiere pago
                return {
                  ...hijo,
                  estadoPago: 'sin_control',
                  requierePago: true
                };
              }
            })
          );

          // Calcular estados
          const pagosPendientes = hijosConPago.filter(hijo => 
            hijo.requierePago && (hijo.estadoPago === 'pendiente' || hijo.estadoPago === 'sin_control')
          ).length;

          const transferenciasEnRevision = hijosConPago.filter(hijo => 
            hijo.estadoPago === 'revision'
          ).length;
          
          setEstadoPagos({
            pagosPendientes,
            transferenciasEnRevision
          });

          setHijos(hijosConPago);
        } catch (error) {
          console.error('Error al obtener los hijos:', error.message);
          setHijos([]);
        }
      };
      fetchHijos();
    }

    const hijoGuardado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
    if (hijoGuardado && hijoGuardado.estado_matricula === 'Activa') {
      setHijoSeleccionado(hijoGuardado);
    }
  }, []);

  useEffect(() => {
    // Función para actualizar el período
    const actualizarPeriodo = () => {
      const mesActual = moment().format('MMMM');
      const mesSiguiente = moment().add(1, 'month').format('MMMM');
      // Capitalizamos la primera letra de cada mes
      const mesActualCap = mesActual.charAt(0).toUpperCase() + mesActual.slice(1);
      const mesSiguienteCap = mesSiguiente.charAt(0).toUpperCase() + mesSiguiente.slice(1);
      setPeriodoActual(`${mesActualCap} - ${mesSiguienteCap}`);
    };

    actualizarPeriodo();

    // Actualizamos el período cada día a la medianoche
    const interval = setInterval(() => {
      actualizarPeriodo();
    }, 86400000); // 24 horas en milisegundos

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const cargarDatosHijo = async () => {
      if (hijoSeleccionado) {
        setLoadingActividades(true);
        
        try {
          // Cargar actividades extracurriculares pendientes
          const actividadesResponse = await obtenerActividadesExtracurricularesPorNino(hijoSeleccionado.id_child);
          if (actividadesResponse.actividades) {
            const actividadesPendientes = actividadesResponse.actividades.filter(
              actividad => !actividad.completada
            ).slice(0, 5);
            setActividadesPendientes(actividadesPendientes);
          }
        } catch (error) {
          console.error('Error al cargar actividades:', error);
        } finally {
          setLoadingActividades(false);
        }
      }
    };

    cargarDatosHijo();
  }, [hijoSeleccionado]);

  useEffect(() => {
    const allCriteriaMet = Object.values(passwordCriteria).every(criteria => criteria);
    setIsValidPassword(allCriteriaMet);
  }, [passwordCriteria]);

  useEffect(() => {
    validatePasswordCriteria(nuevoPassword);
    setPasswordStrength(calculatePasswordStrength(nuevoPassword));
  }, [nuevoPassword]);

  // Effect to re-check if modal should be shown if passwordIsCedula changes in localStorage by other means
  useEffect(() => {
    const handleStorageChange = (event) => {
        // Check if the 'passwordIsCedula' item was the one that changed
        if (event.key === 'passwordIsCedula' || event.key === null) { // null key means clear() or change from another tab/window without specific key
            const currentPasswordIsCedula = localStorage.getItem('passwordIsCedula') === 'true';
            if (currentPasswordIsCedula !== showPasswordChangeModal) {
                setShowPasswordChangeModal(currentPasswordIsCedula);
            }
        }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
        window.removeEventListener('storage', handleStorageChange);
    };
  }, [showPasswordChangeModal]);

  const handleSeleccionarHijo = async (hijo) => {
    setHijoSeleccionado(hijo);
    localStorage.setItem('hijoSeleccionado', JSON.stringify(hijo));
    
    try {
      const response = await obtenerFechasPago(hijo.id_matricula);
      if (response.success && response.data && response.data.length > 0) {
        const fechaLimite = new Date(response.data[0].fecha_limite);
        setFechaLimitePago(fechaLimite);
      } else {
        // Si no hay datos de pago, no establecer fecha límite
        setFechaLimitePago(null);
      }
    } catch (error) {
      console.warn(`No se encontraron fechas de pago para la matrícula ${hijo.id_matricula}:`, error.message);
      setFechaLimitePago(null);
    }
  };

  const handleCambiarHijo = () => {
    setHijoSeleccionado(null);
    localStorage.removeItem('hijoSeleccionado');
  };

  const toggleSubMenu = (menuKey) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const menuItems = hijoSeleccionado
    ? (() => {
        const fechaActual = new Date();
        fechaActual.setHours(0, 0, 0, 0);

        const fechaLimite = fechaLimitePago ? new Date(fechaLimitePago) : null;
        if (fechaLimite) {
          fechaLimite.setHours(0, 0, 0, 0);
        }

        if (fechaLimite && fechaLimite < fechaActual) {
          return [
            { icon: CreditCard, text: 'Pagos', href: '/padre/pagos' },
            { 
              icon: RotateCcw, 
              text: 'Regresar a mi perfil', 
              onClick: () => {
                handleCambiarHijo();
                window.location.href = '/padre';
              }
            },
          ];
        }

        return [
          { icon: LayoutGrid, text: 'Inicio', href: '/padre' },
          { icon: LineChart, text: 'Reportes de progreso', href: '/padre/progreso' },
          { icon: BookOpen, text: 'Actividades Extracurriculares', href: '/padre/actividades-extracurriculares' },
          { icon: CreditCard, text: 'Pagos', href: '/padre/pagos' },
          { icon: GraduationCap, text: `Matrícula de ${hijoSeleccionado.nombre}`, href: `/padre/detalle-matricula` },
          { icon: ClipboardList, text: 'Matricular hijo', href: '/padre/matricular-hijo' },
          { 
            icon: RotateCcw, 
            text: 'Regresar al inicio', 
            onClick: () => {
              handleCambiarHijo();
              window.location.href = '/padre';
            }
          },
        ];
      })()
    : [
        { icon: LayoutGrid, text: 'Inicio', href: '/padre' },
        { icon: ClipboardList, text: 'Matricular hijo', href: '/padre/matricular-hijo' },
        { icon: CreditCard, text: 'Pago múltiple', href: '/padre/pagos-multiple' },
      ];

  const MobileMenu = () => (
    <div className={`lg:hidden fixed inset-0 z-50 ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
      <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Menú</h2>
          <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {menuItems.map((item, index) => (
            <div key={item.href || item.text || index}>
              {item.onClick ? (
                <button
                  onClick={() => {
                    item.onClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.text}</span>
                </button>
              ) : (
                <Link
                  to={item.href}
                  className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.text}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );

  const renderSeleccionHijos = () => {
    if (!hijoSeleccionado) {
      return (
        <div className="p-6">
          {/* Tarjetas informativas centradas */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex flex-wrap justify-center gap-6">
              {/* Tarjeta de Periodo Actual */}
              <div className="w-80 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Período Actual</p>
                    <h3 className="text-xl font-bold mt-1">{periodoActual}</h3>
                    <p className="text-emerald-100 text-xs mt-1">Período de matriculación actual</p>
                  </div>
                  <Calendar className="w-12 h-12 text-emerald-100 opacity-80" />
                </div>
              </div>

              {/* Notificación de Pagos Pendientes */}
              {estadoPagos.pagosPendientes > 0 && (
                <div className="w-80 bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-100 text-sm">Pagos Pendientes</p>
                      <h3 className="text-3xl font-bold mt-1">{estadoPagos.pagosPendientes}</h3>
                      <p className="text-red-100 text-xs mt-1">
                        {estadoPagos.pagosPendientes === 1 ? 'Hijo con pago pendiente' : 'Hijos con pagos pendientes'}
                      </p>
                    </div>
                    <Wallet className="w-12 h-12 text-red-100 opacity-80" />
                  </div>
                  <Link 
                    to="/padre/pagos-multiple"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors duration-200 text-sm font-medium"
                  >
                    Realizar Pagos
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}

              {/* Notificación de Transferencias en Revisión */}
              {estadoPagos.transferenciasEnRevision > 0 && (
                <div className="w-80 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">En Revisión</p>
                      <h3 className="text-3xl font-bold mt-1">{estadoPagos.transferenciasEnRevision}</h3>
                      <p className="text-orange-100 text-xs mt-1">
                        {estadoPagos.transferenciasEnRevision === 1 ? 'Transferencia en revisión' : 'Transferencias en revisión'}
                      </p>
                    </div>
                    <CreditCard className="w-12 h-12 text-orange-100 opacity-80" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-8">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white p-6">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-indigo-600" />
                <h2 className="text-xl font-bold text-gray-800">Perfiles de los niños matriculados</h2>
              </div>
            </div>

            <div className="p-6">
              {hijos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {hijos.map((hijo) => {
                    return (
                      <div
                        key={hijo.id}
                        onClick={() => handleSeleccionarHijo(hijo)}
                        className={`bg-white p-4 rounded-xl border transition-all duration-300 cursor-pointer flex items-center space-x-4 transform hover:scale-[1.02] relative ${
                          hijo.requierePago 
                            ? 'border-red-200 hover:border-red-300 hover:shadow-lg hover:shadow-red-50' 
                            : 'border-slate-200 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50'
                        }`}
                      >
                        {/* Indicador de pago pendiente */}
                        {hijo.requierePago && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                            <Wallet className="w-3 h-3 text-white" />
                          </div>
                        )}
                        
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          hijo.requierePago 
                            ? 'bg-gradient-to-br from-red-100 to-red-200' 
                            : 'bg-gradient-to-br from-blue-100 to-blue-200'
                        }`}>
                          <span className={`text-lg font-medium ${
                            hijo.requierePago ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {hijo.nombre.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-base font-medium text-gray-800">
                              {hijo.nombre} <span className="font-semibold">{hijo.apellido}</span>
                            </h4>
                            {hijo.requierePago && (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                hijo.estadoPago === 'revision' 
                                  ? 'bg-orange-100 text-orange-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {hijo.estadoPago === 'revision' ? 'En Revisión' : 'Pago Pendiente'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Fecha de nacimiento: {new Date(hijo.fecha_nacimiento).toLocaleDateString()}
                          </p>
                          {hijo.requierePago && (
                            <p className="text-xs text-red-600 mt-1">
                              {hijo.estadoPago === 'revision' 
                                ? 'Transferencia en proceso de verificación'
                                : 'Requiere realizar el pago mensual'
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                  <div className="flex flex-col items-center justify-center gap-3 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-gray-900">No hay estudiantes registrados</p>
                      <p className="text-sm text-gray-500 mt-1">No se encontraron estudiantes matriculados</p>
                      <p className="text-xs text-gray-400 mt-1">Registre una nueva matrícula para comenzar</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboardHijo = () => {
    if (hijoSeleccionado) {
      return (
        <div className="p-6 space-y-6">
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Notificación de Estado de Pago del Hijo */}
            {hijoSeleccionado.requierePago && (
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm">Estado de Pago</p>
                    <h3 className="text-xl font-bold mt-1">
                      {hijoSeleccionado.estadoPago === 'revision' ? 'En Revisión' : 'Pago Pendiente'}
                    </h3>
                    <p className="text-red-100 text-xs mt-1">
                      {hijoSeleccionado.estadoPago === 'revision' 
                        ? 'Transferencia en proceso de verificación'
                        : 'Requiere realizar el pago mensual'
                      }
                    </p>
                  </div>
                  <Wallet className="w-12 h-12 text-red-100 opacity-80" />
                </div>
                {hijoSeleccionado.estadoPago !== 'revision' && (
                  <Link 
                    to="/padre/pagos"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors duration-200 text-sm font-medium"
                  >
                    Realizar Pago
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}

            {/* Actividades Extracurriculares Pendientes */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Actividades Extracurriculares</p>
                  <h3 className="text-3xl font-bold mt-1">
                    {actividadesPendientes.length}
                  </h3>
                  <p className="text-blue-100 text-xs mt-1">Pendientes por realizar</p>
                </div>
                <Calendar className="w-12 h-12 text-blue-100 opacity-80" />
              </div>
            </div>

            {/* Estado de Matrícula */}
            <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-violet-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-sm">Estado de Matrícula</p>
                  <h3 className="text-xl font-bold mt-1">
                    {hijoSeleccionado.estado_matricula || 'No disponible'}
                  </h3>
                  <p className="text-violet-100 text-xs mt-1">Estado actual</p>
                </div>
                <GraduationCap className="w-12 h-12 text-violet-100 opacity-80" />
              </div>
            </div>
          </div>

          {/* Lista de Actividades Pendientes */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-800">Actividades Extracurriculares</h2>
                </div>
                <Link 
                  to="/padre/actividades-extracurriculares"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-sm font-medium"
                >
                  Ver todas
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            <div className="p-6">
              {loadingActividades ? (
                <div className="flex justify-center items-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : actividadesPendientes.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay actividades pendientes</p>
                  <p className="text-gray-400 text-sm">Todo está al día</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {actividadesPendientes.map((actividad) => (
                    <div
                      key={actividad.id_actividad}
                      className="p-4 rounded-xl hover:bg-blue-50/50 border border-gray-100 transition-all duration-200 group"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {actividad.titulo}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {actividad.descripcion}
                          </p>
                        </div>
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 group-hover:bg-blue-200 transition-colors">
                          {moment(actividad.fecha_limite).format('DD/MM/YYYY')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="fixed top-0 left-0 right-0 z-50">
        <nav className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
          <div className="max-w-full mx-auto px-4 sm:px-6">
            <div className="flex items-center h-16 relative">
              <div className="flex items-center">
                <button 
                  className="lg:hidden p-2 mr-2 text-gray-600 hover:text-gray-900"
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
                <Link to="/padre" className="group">
                  <div className="flex flex-col">
                    <h1 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                      Centro Infantil
                    </h1>
                    <span className="text-xs sm:text-sm text-slate-600 font-medium">
                      {nombreCentro.split('Centro Infantil ')[1] || 'Mi Jardín'}
                    </span>
                  </div>
                </Link>
              </div>
              
              {hijoSeleccionado && (
                  <div className="absolute left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-sm">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                          Perfil seleccionado
                        </span>
                        <span className="text-sm font-semibold text-slate-800">
                          {hijoSeleccionado.nombre} {hijoSeleccionado.apellido}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              
              <div className="ml-auto"></div>

              <div className="flex items-center gap-2 sm:gap-4">
                <div className="relative">
                  <button
                    onClick={handleChatOpen}
                    data-chat-toggle
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                  >
                    <MessageCircle className="h-5 w-5 text-slate-800" />
                    {!showChat && mensajesNoLeidos > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {mensajesNoLeidos}
                      </div>
                    )}
                  </button>
                </div>
                <div ref={notificationsRef}>
                  <Notificaciones 
                    showNotifications={showNotifications} 
                    setShowNotifications={setShowNotifications} 
                    passwordIsCedula={passwordIsCedula}
                    userType="padre"
                  />
                </div>
                <div className="relative" ref={profileMenuRef}>
                  <div 
                    className="flex items-center gap-3 group cursor-pointer"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="hidden sm:flex flex-col">
                      <span className="text-sm font-medium text-slate-700">{user.nombre}</span>
                      <span className="text-xs text-slate-500">{user.rol}</span>
                    </div>
                  </div>
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 border border-slate-100">
                      <Link
                        to="/padre/perfil-padre"
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        Ver Perfil
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowProfileMenu(false);
                          handleLogout();
                        }}
                        className="lg:hidden w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-50"
                      >
                        Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="hidden lg:block text-slate-600 hover:text-red-600 transition-all duration-300"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <div className="pt-16 flex">
        <aside className="hidden lg:fixed lg:block w-64 bg-white/80 backdrop-blur-sm border-r border-slate-100 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4">
            <nav className="p-4 space-y-1">
          {menuItems.map((item, index) => (
            <div key={item.href || item.text || index}>
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm transform hover:translate-x-1"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.text}</span>
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm transform hover:translate-x-1"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.text}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-500"> 2023 Mi Jardín</p>
              <p className="text-[10px] text-gray-400">Todos los derechos reservados</p>
            </div>
          </div>
        </aside>

        <main className="flex-1 lg:ml-64 p-4 sm:p-8">
          <div className="animate-fadeIn">
            {location.pathname === '/padre' ? (
              hijoSeleccionado ? renderDashboardHijo() : renderSeleccionHijos()
            ) : (
              <div className="p-6">
                <Outlet />
              </div>
            )}
          </div>
        </main>

        {/* Mobile Menu */}
        <MobileMenu />

        {showChat && (
          <div className="fixed inset-0 z-50 flex">
            <div className="absolute inset-0" onClick={handleChatClose}></div>
            <div className="relative ml-auto">
              <Chat 
                user={user}
                onClose={handleChatClose}
                userId={user.id}
                userType="padre"
                onMessageRead={handleMessageRead}
              />
            </div>
          </div>
        )}

        <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        <Chatbot user={user} className={showChat ? 'hidden' : ''} />

        {/* Password Change Modal */}
        {showPasswordChangeModal && (
          <div className="fixed inset-0 bg-slate-900 bg-opacity-80 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-fadeInScale">
              <div className="flex items-center justify-start mb-6">
                <Lock size={28} className="text-indigo-600 mr-3" />
                <h2 className="text-2xl font-bold text-slate-800">Cambio de Contraseña Obligatorio</h2>
              </div>
              <p className="text-slate-600 mb-6 text-sm">
                Por motivos de seguridad, es necesario que establezca una nueva contraseña.
              </p>
              <form onSubmit={handlePasswordChangeSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="nuevoPassword">
                    Nueva Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="nuevoPassword"
                      name="nuevoPassword"
                      value={nuevoPassword}
                      onChange={(e) => setNuevoPassword(e.target.value)}
                      className={`w-full px-4 py-2.5 bg-white border rounded-lg shadow-sm focus:ring-2 transition-colors duration-150 text-sm 
                                  ${passwordChangeError && (nuevoPassword === '' || !isValidPassword || passwordStrength < 75) ? 'border-red-400 ring-red-200 focus:ring-red-400 focus:border-red-400' 
                                  : 'border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-600 transition-colors"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {nuevoPassword.length > 0 && (
                      <div className="mt-2.5 space-y-1.5">
                          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                              <div
                                  className={`h-full rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength)}`}
                                  style={{ width: `${passwordStrength}%` }}
                              />
                          </div>
                          <p className={`text-xs font-medium ${passwordStrength < 40 ? 'text-red-600' : passwordStrength < 80 ? 'text-orange-500' : 'text-green-600'}`}>
                              Seguridad: {getPasswordStrengthText(passwordStrength)}
                          </p>
                      </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs mt-1">
                  <PasswordRequirement met={passwordCriteria.length} text="Mínimo 8 caracteres" />
                  <PasswordRequirement met={passwordCriteria.letter} text="Incluye letras (a-z, A-Z)" />
                  <PasswordRequirement met={passwordCriteria.number} text="Incluye números (0-9)" />
                  <PasswordRequirement met={passwordCriteria.special} text="Incluye especiales (!@#)" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="confirmarPassword">
                    Confirmar Nueva Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmarPassword"
                      name="confirmarPassword"
                      value={confirmarPassword}
                      onChange={(e) => setConfirmarPassword(e.target.value)}
                      className={`w-full px-4 py-2.5 bg-white border rounded-lg shadow-sm focus:ring-2 transition-colors duration-150 text-sm 
                                  ${passwordChangeError && confirmarPassword !== nuevoPassword ? 'border-red-400 ring-red-200 focus:ring-red-400 focus:border-red-400' 
                                  : 'border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                      disabled={isPasswordSuccessfullyChanged}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirmarPassword && nuevoPassword && confirmarPassword !== nuevoPassword && (
                    <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden.</p>
                  )}
                </div>

                {passwordChangeError && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200 text-center">
                    {passwordChangeError}
                  </p>
                )}

                {/* Post-submission API response message */}
                {apiResponseMessage.text && (
                  <p className={`text-sm p-3 rounded-md border text-center 
                    ${apiResponseMessage.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' 
                                                            : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {apiResponseMessage.text}
                  </p>
                )}

                <button 
                  type={isPasswordSuccessfullyChanged ? "button" : "submit"}
                  onClick={isPasswordSuccessfullyChanged ? handleCloseModalAfterSuccess : undefined}
                  disabled={!isPasswordSuccessfullyChanged && (!nuevoPassword || !confirmarPassword || !isValidPassword || passwordStrength < 75 || nuevoPassword !== confirmarPassword)}
                  className={`w-full text-white font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white flex items-center justify-center 
                              ${isPasswordSuccessfullyChanged ? 'bg-slate-500 hover:bg-slate-600 focus:ring-slate-400' 
                                                              : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                >
                  <Lock size={18} className="mr-2" />
                  {isPasswordSuccessfullyChanged ? 'Cerrar' : 'Cambiar Contraseña'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;